import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { Building2, FlaskConical, MapPin, PanelsTopLeft, Radar, Rocket } from "lucide-react";
import { Navigate, NavLink } from "react-router-dom";

import { PageHeader } from "@/shared/ui/PageHeader";
import { forbiddenPathFor } from "@/shared/lib/forbiddenReason";
import { cn } from "@/shared/lib/utils";
import { useBrand } from "@/shared/brand";

import { isAcqFixtureData, subscribeAcqFixtureFlag } from "../api/acqService";
import { qikeEnabledOnce } from "../api/qikeService";
import { anyExternalToolEnabled } from "../model/externalToolModules";
import { useAcqModuleGate } from "../model/useAcqModuleGate";

/**
 * 获客舱外壳(2026-09-17 重构)。
 *
 * ═══ 形态变了,不是改了个皮 ═══════════════════════════════════════════════
 * 旧:五张平级卡的工具墙 —— 用户自己挑一个工具进去搜。
 * 新:**每天自动送到面前的一份单子** —— 「今天该做的这几件事」(打电话 / 发邮件 / 加微信 /
 * 上门 / 去回答 / 去私信 / 先观察,由每张卡的 suggestedAction 定)。
 * 地图搜索和外链三卡都退成二级入口(契约 §8 / §9②:搜索框不当主入口,
 * 外链从"第四种获客方式"变成"动作的出口")。
 *
 * ★ 0917 v2 又改了一次落地口径:**没订阅、也没单子的用户落在「去哪里找」**
 *   (`AcqLanding` 分流)。对一个手上零线索的新用户来说,「今日商机」是一页空的,
 *   而空页面教给他的是"这个功能没东西" —— 他该看到的是那 15 种可以去找的地方。
 * ═══════════════════════════════════════════════════════════════════════════
 */
/**
 * ★ 0917 v2:顺序是**去哪里找 · 今日商机 · 线索库 · 我的生意**(契约 §12.8)。
 *   「去哪里找」排第一不是偏好问题:新用户手上一条单子都没有,今日商机对他是空的 ——
 *   把空页面摆在第一格,他学到的是"这个功能没东西"。
 *   「信号订阅」整页退役,盯什么已经搬到每一种发现自己的卡上。
 */
const TABS = [
  { to: "/ai-huoke/discover", label: "去哪里找", end: false },
  { to: "/ai-huoke", label: "今日商机", end: true },
  { to: "/ai-huoke/leads", label: "线索库", end: false },
  { to: "/ai-huoke/business", label: "我的生意", end: false },
];

/** 二级入口:不与获客能力平级摆首页,但一直够得着。 */
const SECONDARY = [
  { to: "/ai-huoke/company", label: "企业库", icon: Building2 },
  // 1025 启客拓客:平台没配启客 = 入口不出(见 useQikeEnabled)
  { to: "/ai-huoke/qike", label: "拓客工作台", icon: Radar },
  { to: "/ai-huoke/map", label: "按地图找", icon: MapPin },
  { to: "/ai-huoke/tools", label: "外部工具", icon: PanelsTopLeft },
];

export function AcqShell({
  subtitle,
  actions,
  children,
}: {
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  // B2 #20(0927 K2 实测):未开通获客舱的租户直访 /ai-huoke 渲染舱壳 + 后端原句「当前租户未开通该模块」,
  // /ai-huoke/tools 三张卡完整可见。接口 403 MODULE_NOT_ENABLED → 整舱落 403 页(含 tools 等不调接口的子路由)。
  const blocked = useAcqModuleGate();
  // OEM 贴牌(§2.4):三张外链卡全被品牌关掉 → 「外部工具」二级入口不出。
  const brand = useBrand();
  const qikeEnabled = useQikeEnabled();
  const secondary = SECONDARY.filter(
    (s) =>
      (s.to !== "/ai-huoke/tools" || anyExternalToolEnabled(brand.modules)) &&
      (s.to !== "/ai-huoke/qike" || qikeEnabled)
  );
  if (blocked) return <Navigate to={forbiddenPathFor(blocked)} replace />;
  return (
    // ★ 裸 space-y-6,不带自己的 p-*:页面留白由 AdminLayout 的 main 统一给
    //   (2026-09-01 用户报过一次「获客舱没跟其他页面对齐左上角」= 双重内边距)。
    <div className="space-y-6">
      <PageHeader icon={Rocket} title="获客舱" subtitle={subtitle} actions={actions} />

      <FixtureBanner />

      {/* ★ flex-wrap:窄屏让二级入口整组掉到第二行,而不是把主 tab 压扁。 */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b">
        <nav className="-mb-px flex min-w-0 gap-1 overflow-x-auto" aria-label="获客舱">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              // 1005:标记「是点页签进来的」—— 落地分流据此决定给今日商机空态还是转去「去哪里找」
              state={t.to === "/ai-huoke" ? { acqTab: "today" } : undefined}
              className={({ isActive }) =>
                cn(
                  "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition-colors",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-1 pb-1.5">
          {secondary.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </div>
      </div>

      {children}
    </div>
  );
}

/**
 * ★★「样例数据」横幅 —— fixture 回落**不许静默**(0916 纪律)。
 *
 *   静默回落最坏的形态不是"数据是假的",是**一个坏掉的后端在页面上看起来是好的**:
 *   联调时对着一屏编出来的卡讨论排版,而真正该红的那口从头到尾没人看见。
 *   旗子只从 false → true(这一次会话里已经骗过一次人,后面就一直标着),刷新即清。
 *
 * ★ `useSyncExternalStore` 而不是 effect + state:回落发生在 `acqService` 里,
 *   页面各处都可能触发,订阅式读才不会漏掉别的页面触发的那一次。
 */
function FixtureBanner() {
  const on = useSyncExternalStore(subscribeAcqFixtureFlag, isAcqFixtureData, () => false);
  if (!on) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-xs font-semibold text-warning">
      <FlaskConical className="h-4 w-4 shrink-0" />
      样例数据 —— 这一页至少有一口没连上后端，走的是本地桩。上面的公司、条数和价钱都不是真的。
    </div>
  );
}

/** 拓客工作台入口显不显(平台配了启客才显);问一次,失败当没开通。 */
function useQikeEnabled(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let alive = true;
    void qikeEnabledOnce().then((v) => alive && setOn(v));
    return () => {
      alive = false;
    };
  }, []);
  return on;
}
